
export const normalizeValue = (val: any): any => {
    if (val === null || val === undefined) return '';
    return val;
};

export const mergePageVariables = (page: any, userVariables: Record<string, any> = {}): Record<string, any> => {
    const globalVariables: Record<string, any> = {
        showAuthor: true,
        showDate: true,
        showCategories: true,
        showActions: true,
        showParent: true,
        showTitle: true,
        showToc: true,
        showLastUpdated: true,
        showFooter: true,
        width: '',
        padding: 'px-2 md:p-8'
    };

    // 0. User Variables (Base)
    Object.entries(userVariables).forEach(([k, v]) => {
        globalVariables[k] = normalizeValue(v);
    });

    // Category Variables
    if (page.category_paths && Array.isArray(page.category_paths)) {
        // Flatten all categories across all paths
        const allCategories = page.category_paths.flat();
        for (const cat of allCategories) {
            if (cat.meta?.variables) {
                Object.entries(cat.meta.variables).forEach(([k, v]) => {
                    globalVariables[k] = normalizeValue(v);
                });
            }
        }
    }

    // Type Values
    if (page.meta?.typeValues) {
        Object.values(page.meta.typeValues).forEach((typeVal: any) => {
            if (typeVal && typeof typeVal === 'object') {
                Object.entries(typeVal).forEach(([k, v]) => {
                    globalVariables[k] = normalizeValue(v);
                });
            }
        });
    }

    // Page Variables (Highest Precedence)
    if (page.meta?.variables) {
        Object.entries(page.meta.variables).forEach(([k, v]) => {
            globalVariables[k] = normalizeValue(v);
        });
    }

    return globalVariables;
};

/** Default app-shell cap. Empty `width` keeps this. */
export const DEFAULT_PAGE_MAX_WIDTH = '1400px';

/**
 * Resolve the page `width` variable to a CSS max-width.
 * `full` (also `full-width`, `none`, `100%`) means no cap.
 * A bare number is pixels. Anything else that is not a length falls back to the default.
 */
export const pageShellMaxWidth = (raw: unknown): string | null => {
    const value = String(raw ?? '').trim();
    if (!value) return DEFAULT_PAGE_MAX_WIDTH;
    const lower = value.toLowerCase();
    if (lower === 'full' || lower === 'full-width' || lower === 'none' || lower === '100%') return null;
    if (/^\d+(\.\d+)?$/.test(value)) return `${value}px`;
    if (/^\d+(\.\d+)?(px|rem|em|vw|vh|%|ch)$/i.test(value)) return value;
    return DEFAULT_PAGE_MAX_WIDTH;
};

/** Classes on the page content wrapper, above the layout containers. */
export const DEFAULT_PAGE_PADDING = 'px-2 md:p-8';

/**
 * Resolve the page `padding` variable.
 * Empty, `none`, or `blank` removes the inset. Any other value is used as the class string.
 * A value that is not a class list falls back to the default.
 */
export const pageContentPadding = (raw: unknown): string => {
    const value = String(raw ?? '').trim();
    const lower = value.toLowerCase();
    if (!value || lower === 'none' || lower === 'blank') return '';
    if (!/^[\w\s:/.[\]%!-]+$/.test(value)) return DEFAULT_PAGE_PADDING;
    return value;
};
